import { Markup } from '@data-access/connect/tc-tcps';
import {
  ClassificationScheme,
  DEFAULT_POINT_DENSITY_BIAS,
  isValidPointDensityBias,
  POINT_CLOUD_STYLING_DEFAULTS,
  PointCloudClassDefinition,
  PointCloudStyling,
  PointCloudStylingClearance,
  PointCloudStylingIntensityGradient,
  PointCloudStylingType,
  Scan3dStyle,
} from '@trimble/types';

/** Length of a hex color without alpha (RRGGBB) */
const HEX_COLOR_LENGTH = 6;

/**
 * Determines if a markup is a measurement type
 * @param markup The markup object to check
 * @returns True if the markup is a measurement type, false otherwise
 */
export const isMeasurement = (markup: Markup): boolean => {
  const measurementTypes = [
    'measure', // Point measurement
    'measure_with_pick', // Line measurement
    'angle', // Angle measurement
  ];

  return measurementTypes.includes(markup.type);
};

/**
 * Ensures a color string has a '#' prefix
 */
function ensureHashPrefix(color: string): string {
  return color.startsWith('#') ? color : `#${color}`;
}

/**
 * Converts an RGBA color string (with optional alpha) to a hex color with '#' prefix
 * Removes the alpha channel if present (e.g., "7F7F7FFF" -> "#7F7F7F")
 */
function convertRgbaToHexColor(rgba: string): string {
  const colorWithoutHash = rgba.startsWith('#') ? rgba.slice(1) : rgba;
  const colorWithoutAlpha = colorWithoutHash.substring(0, HEX_COLOR_LENGTH);
  return `#${colorWithoutAlpha}`;
}

/**
 * Converts classification schemes from Connect API format to PointCloudClassDefinition array
 */
function convertClassificationSchemes(
  schemes: ClassificationScheme[]
): PointCloudClassDefinition[] {
  return schemes.map((scheme) => ({
    id: scheme.id,
    color: convertRgbaToHexColor(scheme.rgba),
    visibility: scheme.visible,
  }));
}

/**
 * Converts Scan3dStyle from the API to PointCloudStyling format used by the renderer
 */
export function convertScan3dStyleToPointCloudStyling(
  scan3dStyle: Scan3dStyle,
  defaults: PointCloudStyling = POINT_CLOUD_STYLING_DEFAULTS
): PointCloudStyling {
  // Although the properties on Scan3dStyle are required, the API may not return them all.
  // This is because of additional properties added after the View was created.
  // Therefore, we need to check if the properties are defined before using them or use the defaults.
  const gradient: PointCloudStylingIntensityGradient | undefined =
    // eslint-disable-next-line sonarjs/different-types-comparison -- scan3d style fields can be missing at runtime despite the declared type
    scan3dStyle.intensityStartColor !== undefined &&
    // eslint-disable-next-line sonarjs/different-types-comparison -- scan3d style fields can be missing at runtime despite the declared type
    scan3dStyle.intensityEndColor !== undefined &&
    // eslint-disable-next-line sonarjs/different-types-comparison -- scan3d style fields can be missing at runtime despite the declared type
    scan3dStyle.intensityOffset !== undefined
      ? {
          startColor: ensureHashPrefix(scan3dStyle.intensityStartColor),
          endColor: ensureHashPrefix(scan3dStyle.intensityEndColor),
          offset: scan3dStyle.intensityOffset,
        }
      : defaults.intensity?.gradient;

  const clearance: PointCloudStylingClearance | undefined =
    // eslint-disable-next-line sonarjs/different-types-comparison -- scan3d style fields can be missing at runtime despite the declared type
    scan3dStyle.clearanceBands !== undefined &&
    scan3dStyle.clearanceBands.length > 0 &&
    scan3dStyle.clearanceBands.every(
      // eslint-disable-next-line sonarjs/different-types-comparison -- scan3d style fields can be missing at runtime despite the declared type
      (band) => band.start !== undefined && band.color !== undefined
    ) &&
    // eslint-disable-next-line sonarjs/different-types-comparison -- scan3d style fields can be missing at runtime despite the declared type
    scan3dStyle.clearanceDefaultColor !== undefined
      ? {
          bands: scan3dStyle.clearanceBands.map((band) => ({
            start: band.start,
            color: ensureHashPrefix(band.color),
          })),
          defaultColor: ensureHashPrefix(scan3dStyle.clearanceDefaultColor),
          defaultTransparent:
            scan3dStyle.clearanceDefaultTransparent ??
            defaults.clearance?.defaultTransparent,
        }
      : defaults.clearance;

  const styling: Partial<PointCloudStyling> = {
    stylingType: scan3dStyle.renderingMode ?? defaults.stylingType,

    point: {
      budget: scan3dStyle.pointBudget ?? defaults.point?.budget,
      // Existing saved views can hold pointDensity 0.
      densityBias: isValidPointDensityBias(scan3dStyle.pointDensity)
        ? scan3dStyle.pointDensity
        : defaults.point?.densityBias,
      size: scan3dStyle.pointSize ?? defaults.point?.size,
    },

    eyeDomeLighting: {
      enabled:
        scan3dStyle.showEyeDomeLighting ?? defaults.eyeDomeLighting?.enabled,
    },

    intensity: {
      blendIntensity:
        scan3dStyle.blendIntensity ?? defaults.intensity?.blendIntensity,
      gradient,
    },

    clearance,

    elevation: {
      reference:
        scan3dStyle.elevationReference ?? defaults.elevation?.reference,
      tolerance:
        scan3dStyle.elevationTolerance ?? defaults.elevation?.tolerance,
      bandCount:
        scan3dStyle.elevationBandCount ?? defaults.elevation?.bandCount,
      offset: scan3dStyle.elevationOffset ?? defaults.elevation?.offset,
    },
  };

  // Add classification if schemes exist
  if (
    scan3dStyle.classificationSchemes &&
    scan3dStyle.classificationSchemes.length > 0
  ) {
    styling.classification = {
      classDefinitions: convertClassificationSchemes(
        scan3dStyle.classificationSchemes
      ),
    };
  }

  return styling;
}

/**
 * Converts PointCloudStyling format to Scan3dStyle for the API
 * Reverse of convertScan3dStyleToPointCloudStyling
 */
export function convertPointCloudStylingToScan3dStyle(
  styling: PointCloudStyling
): Scan3dStyle {
  const gradient = styling.intensity?.gradient;
  const clearance = styling.clearance;
  const elevation = styling.elevation;
  const point = styling.point;

  // Convert class definitions back to classification schemes
  const classificationSchemes: ClassificationScheme[] =
    styling.classification?.classDefinitions?.map((def) => ({
      id: def.id,
      name: `Class ${def.id}`,
      rgba: def.color.replace('#', '') + 'FF', // Add full alpha
      visible: def.visibility,
      predefined: false,
    })) ?? [];

  // Use defaults safely without non-null assertions
  const defaults = POINT_CLOUD_STYLING_DEFAULTS;
  const defaultPoint = defaults.point ?? {
    budget: 30000000,
    densityBias: DEFAULT_POINT_DENSITY_BIAS,
    size: 2,
  };
  const defaultIntensity = defaults.intensity ?? {
    blendIntensity: false,
    gradient: { startColor: '#000000', endColor: '#ffffff', offset: 0.5 },
  };
  const defaultGradient = defaultIntensity.gradient ?? {
    startColor: '#000000',
    endColor: '#ffffff',
    offset: 0.5,
  };
  const defaultEyeDome = defaults.eyeDomeLighting ?? { enabled: true };
  const defaultClearance = defaults.clearance ?? {
    defaultTransparent: false,
    defaultColor: '#C8A2C8',
    bands: [
      { start: -0.4, color: '#ff0000' },
      { start: 0, color: '#ff7f00' },
      { start: 0.1, color: '#ffff00' },
      { start: 0.2, color: '#00ff00' },
      { start: 0.3, color: '#0000ff' },
      { start: 0.4, color: '#000000' },
    ],
  };
  const defaultElevation = defaults.elevation ?? {
    reference: undefined,
    tolerance: 1,
    bandCount: 1,
    offset: 0,
  };

  return {
    renderingMode:
      styling.stylingType ?? defaults.stylingType ?? PointCloudStylingType.Rgb,
    blendIntensity:
      styling.intensity?.blendIntensity ??
      defaultIntensity.blendIntensity ??
      false,
    showEyeDomeLighting:
      styling.eyeDomeLighting?.enabled ?? defaultEyeDome.enabled ?? true,
    pointBudget: point?.budget ?? defaultPoint.budget ?? 0,
    pointDensity: isValidPointDensityBias(point?.densityBias)
      ? point.densityBias
      : (defaultPoint.densityBias ?? DEFAULT_POINT_DENSITY_BIAS),
    pointSize: point?.size ?? defaultPoint.size ?? 0,
    classificationSchemes,
    intensityOffset: gradient?.offset ?? defaultGradient.offset,
    intensityStartColor:
      gradient?.startColor?.replace('#', '') ??
      defaultGradient.startColor.replace('#', ''),
    intensityEndColor:
      gradient?.endColor?.replace('#', '') ??
      defaultGradient.endColor.replace('#', ''),
    clearanceDefaultTransparent:
      clearance?.defaultTransparent ??
      defaultClearance.defaultTransparent ??
      false,
    clearanceDefaultColor:
      clearance?.defaultColor?.replace('#', '') ??
      defaultClearance.defaultColor?.replace('#', '') ??
      '',
    clearanceBands:
      clearance?.bands?.map((band) => ({
        start: band.start,
        color: band.color.replace('#', ''),
      })) ??
      (defaultClearance.bands ?? []).map((band) => ({
        start: band.start,
        color: band.color.replace('#', ''),
      })),
    elevationReference: elevation?.reference ?? defaultElevation.reference ?? 0,
    elevationTolerance: elevation?.tolerance ?? defaultElevation.tolerance ?? 0,
    elevationBandCount: elevation?.bandCount ?? defaultElevation.bandCount ?? 0,
    elevationOffset: elevation?.offset ?? defaultElevation.offset ?? 0,
  };
}
